//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';

import DateRangePickerPanel from '@components/date-range-picker/DateRangePickerPanel';
import {
  IAliasedDateRange,
  getReadableDateRangeValue,
} from '@components/date-range-picker/dateRangePickerModels';
import ButtonPopover from '@components/molecules/button-popover/ButtonPopover';
import { usePopover } from '@components/molecules/popover/usePopover';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { ITrackableComponent } from '@utils/withTracking';

type IPopoverProps = Pick<
  React.ComponentProps<typeof ButtonPopover>,
  'appendTarget' | 'placement'
>;

interface IOwnProps extends ITrackableComponent, IPopoverProps {
  aliases?: IAliasedDateRange[];
  className?: string;
  footnote?: React.ReactNode;
  isDisabled?: boolean;
  isInitialEndDateDisabled?: boolean;
  maxDate?: DateString;
  maxDateRange?: number;
  minDate?: DateString;
  onDateRangeChanged: (dateRange: IAliasedDateRange) => void;
  timeZone: TimeZoneId;
  value: IAliasedDateRange;
}

const DateRangePicker = ({
  appendTarget,
  className,
  isDisabled,
  isInitialEndDateDisabled = false,
  onDateRangeChanged,
  placement = 'bottom-start',
  trackingId,
  value,
  ...pickerProps
}: IOwnProps): JSX.Element => {
  const [connectPopover, { closePopover }] = usePopover();
  // Using thia because popoverMonitor isn't being updated with
  // with visibility state when toggling popover
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ButtonPopover
      appendTarget={appendTarget}
      className={className}
      connectPopover={connectPopover}
      content={
        <DateRangePickerPanel
          initialValue={value}
          isInitialEndDateDisabled={isInitialEndDateDisabled}
          isOpen={isOpen}
          onCancelClicked={() => {
            closePopover();
            setIsOpen(false);
          }}
          onSetDateClicked={(alias) => {
            onDateRangeChanged(alias);
            closePopover();
            setIsOpen(false);
          }}
          {...pickerProps}
        />
      }
      isDisabled={isDisabled}
      onTogglePopover={(isOpen, _) => {
        setIsOpen(isOpen);
      }}
      placeholderText='Select a date range'
      placement={placement}
      trackingId={trackingId}
      valueText={getReadableDateRangeValue(value)}
    ></ButtonPopover>
  );
};

export default DateRangePicker;
